/**
 * Component Registry for Aion Management Theme
 * 
 * Extends the base belmont theme registry with Aion Management specific overrides.
 */

import type { ComponentRegistry } from '@vibesites/runtime';
import { registry as baseRegistry } from '@vibesites/theme-belmont/registry';
import { Hero } from './components/Hero';
import { Header } from './components/Header';
import { AmenitiesList } from './components/AmenitiesList';
import { HeroSolidBG } from './components/HeroSolidBG';
import { FloorplansList } from './components/FloorplansList';
import { NeighborhoodShowcase } from './components/NeighborhoodShowcase';
import { NeighborhoodHighlights } from './components/NeighborhoodHighlights';
import { NeighborhoodMap } from './components/NeighborhoodMap';
import { PropertyOverview } from './components/PropertyOverview';
import { PinnedCallouts } from './components/PinnedCallouts';
import { GalleryApartments } from './components/GalleryApartments';
import { PetPolicyCallout } from './components/PetPolicyCallout';
import { ResidentAccess } from './components/ResidentAccess';
import { ApartmentSyncSection } from './components/ApartmentSyncSection';
import { InteractiveMapSection } from './components/InteractiveMapSection';
import { VirtualTourEmbed } from './components/VirtualTourEmbed';
import { SitemapContent } from './components/SitemapContent';
import { ContactForm } from './components/ContactForm';
import { ContactMap } from './components/ContactMap';
import { Footer } from './components/Footer';
import { PrivacyPolicyContent } from './components/PrivacyPolicyContent';
import { AccessibilityStatement } from './components/AccessibilityStatement';
import { ThankYouContent } from './components/ThankYouContent';
import { HyperlocalPageCopy } from './components/HyperlocalPageCopy';
import { FeeSheet } from './components/FeeSheet';
import { SpecialOfferBanner } from './components/SpecialOfferBanner';

/**
 * Aion Management specific component overrides.
 * These components have been customized for the Aion Management brand.
 */
const aionmanagementOverrides: Partial<ComponentRegistry> = {
  Hero,
  Header,
  AmenitiesList,
  ApartmentSyncSection,
  HeroSolidBG,
  FloorplansList,
  NeighborhoodShowcase,
  NeighborhoodHighlights,
  NeighborhoodMap,
  PropertyOverview,
  PinnedCallouts,
  GalleryApartments,
  PetPolicyCallout,
  ResidentAccess,
  InteractiveMapSection,
  VirtualTourEmbed,
  SitemapContent,
  ContactForm,
  ContactMap,
  Footer,
  PrivacyPolicyContent,
  AccessibilityStatement,
  ThankYouContent,
  HyperlocalPageCopy,
  FeeSheet,
  SpecialOfferBanner,
};

/**
 * Final merged registry for Aion Management properties.
 * Inherits all components from the belmont theme, with Aion Management overrides.
 */
export const registry: ComponentRegistry = {
  ...baseRegistry,
  ...aionmanagementOverrides,
};

export default registry;
